import { Component, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { ApiService } from '../core/api.service';

@Component({
  selector: 'app-import-upload',
  imports: [FormsModule],
  template: `
    <section class="card">
      <h1>Import CSV</h1>
      <p class="hint">This will <b>delete all existing reservations</b> before import. The CSV must be UTF-8, comma-delimited, with a header row.</p>

      @if (message()) {
        <div class="msg" [class.err]="isError()">{{ message() }}</div>
      }

      @if (activePropertyCode()) {
        <div class="ctx">Active property: <code>{{ activePropertyCode() }}</code> · {{ activePropertyName() }}</div>
      }

      <form class="form" (submit)="upload($event)">
        <label>
          <span>VM Property ID</span>
          <input [value]="activePropertyCode()" type="text" readonly>
        </label>
        <label>
          <span>CSV file</span>
          <input name="file" type="file" accept=".csv,text/csv" required (change)="onFileSelected($event)">
          <small>Only .csv is allowed. Format validation happens on the next step.</small>
        </label>
        <label>
          <span>Type CLEAR to confirm wiping existing reservations</span>
          <input [(ngModel)]="confirmClear" name="confirm" type="text" required placeholder="CLEAR">
        </label>
        <div class="actions">
          <button type="submit" [disabled]="busy() || !file">{{ busy() ? 'Working…' : 'Continue to Mapping' }}</button>
        </div>
      </form>
    </section>
  `,
  styles: `
    .card{max-width:760px;margin:0 auto;background:#fff;border:1px solid var(--line);border-radius:14px;padding:18px;display:grid;gap:14px}
    h1{margin:0}
    .hint,small{color:var(--muted)}
    .form{display:grid;gap:14px}
    label{display:grid;gap:6px;font-weight:600;color:#24334a}
    span{font-size:14px}
    input{height:42px;border:1px solid #c8d2e1;border-radius:10px;padding:0 12px}
    input[type="file"]{padding:8px 12px;height:auto}
    .actions{display:flex;gap:10px;flex-wrap:wrap}
    .ctx{padding:10px;border-radius:10px;background:#edf3ff;border:1px solid #c7d7f5;color:#24416a}
    button{height:40px;border:0;border-radius:10px;background:var(--primary);color:#fff;font-weight:700;padding:0 14px;cursor:pointer}
    .msg{padding:10px 12px;border-radius:10px;background:#eef4ff;color:#0a2f6b;border:1px solid #c7d7f5}
    .msg.err{background:#fdecec;color:#8a1f1f;border-color:#f2c4c4}
  `,
})
export class ImportUploadComponent {
  private readonly api = inject(ApiService);
  private readonly router = inject(Router);

  readonly busy = signal(false);
  readonly message = signal('');
  readonly isError = signal(false);

  readonly activePropertyCode = signal('');
  readonly activePropertyName = signal('');
  confirmClear = '';
  file: File | null = null;

  constructor() {
    void this.loadContext();
  }

  private async loadContext(): Promise<void> {
    try {
      const ctx = await firstValueFrom(this.api.get<{ activeProperty: { code: string; name: string } | null }>('/context/property'));
      this.activePropertyCode.set(ctx.activeProperty?.code || '');
      this.activePropertyName.set(ctx.activeProperty?.name || '');
    } catch {
      this.activePropertyCode.set('');
      this.activePropertyName.set('');
    }
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.file = input.files?.[0] ?? null;
  }

  async upload(event: Event): Promise<void> {
    event.preventDefault();
    if (!this.file) return;
    if (this.confirmClear.trim().toUpperCase() !== 'CLEAR') {
      this.isError.set(true);
      this.message.set('Type CLEAR to confirm wiping existing reservations.');
      return;
    }

    this.busy.set(true);
    this.message.set('');
    this.isError.set(false);
    try {
      await firstValueFrom(this.api.post('/import/reset', {}));
      const form = new FormData();
      form.append('file', this.file);
      form.append('confirm', this.confirmClear.trim());
      const res = await firstValueFrom(this.api.post<{ ok: boolean; error?: string }>('/import/upload', form));
      if (!res.ok) {
        this.isError.set(true);
        this.message.set(res.error || 'Upload failed.');
        return;
      }
      window.dispatchEvent(new Event('settings-changed'));
      await this.router.navigateByUrl('/import/vm-auth?next=map');
    } catch {
      this.isError.set(true);
      this.message.set('Upload failed.');
    } finally {
      this.busy.set(false);
    }
  }
}
